import { useEffect, useState, useCallback, useRef } from "react";
import { SlidersHorizontal, ArrowDownWideNarrow, Loader2, TrendingDown, Gift, X, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Ticker from "../components/site/Ticker";
import Navbar from "../components/site/Navbar";
import Hero from "../components/site/Hero";
import MoversStrip from "../components/site/MoversStrip";
import MarketBoard from "../components/site/MarketBoard";
import SpotlightBanner from "../components/site/SpotlightBanner";
import BundleOffer from "../components/site/BundleOffer";
import FilterSidebar from "../components/site/FilterSidebar";
import ProductCard from "../components/site/ProductCard";
import PreviewModal from "../components/site/PreviewModal";
import AutopilotSection from "../components/site/AutopilotSection";
import Newsletter from "../components/site/Newsletter";
import Footer from "../components/site/Footer";
import ExitIntentPopup from "../components/site/ExitIntentPopup";
import { useLiveMarket } from "../context/LiveMarketContext";
import { useWatchlist } from "../context/WatchlistContext";

const SORTS = [
  { label: "Most Popular", value: "popular" },
  { label: "Highest Savings", value: "savings" },
  { label: "Top Rated", value: "rating" },
  { label: "Price: Low", value: "price_low" },
  { label: "Price: High", value: "price_high" },
];

export default function Storefront() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [samplePosts, setSamplePosts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [refBanner, setRefBanner] = useState(null);
  const [rewardBanner, setRewardBanner] = useState(null);
  const [filters, setFilters] = useState({
    software: "All",
    tier: "All",
    min_rating: 0,
    sort: "popular",
  });

  const { onDrop } = useLiveMarket();
  const { ids } = useWatchlist();
  const productsRef = useRef([]);
  const watchRef = useRef(ids);
  const lastAlertRef = useRef({});
  productsRef.current = products;
  watchRef.current = ids;

  useEffect(() => {
    api.stats().then(setStats).catch((err) => console.error("Failed to load stats", err?.message || err));
    api.socialPosts().then((p) => setSamplePosts(p)).catch((err) => console.error("Failed to load social posts", err?.message || err));
  }, []);

  // Referral capture: ?ref=CODE applies a friend's discount at checkout
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("ref");
    if (code) {
      api.validateReferral(code, true).then((r) => {
        if (r.valid) {
          try { localStorage.setItem("tt_ref", r.code); } catch {}
          setRefBanner({ code: r.code, discount_pct: r.discount_pct });
        }
      }).catch((err) => console.error("Referral validation failed", err?.message || err));
      try { window.history.replaceState({}, "", window.location.pathname); } catch (err) { console.warn("Could not clean ref param from URL", err?.message || err); }
      return;
    }
    try {
      const stored = localStorage.getItem("tt_ref");
      if (stored) setRefBanner({ code: stored, discount_pct: 10 });
    } catch {}
  }, []);

  // Reward auto-apply banner: a redeemed referral reward applies at checkout
  useEffect(() => {
    try {
      const code = localStorage.getItem("tt_reward_code");
      const amt = parseFloat(localStorage.getItem("tt_reward_amt") || "0");
      if (code && amt > 0) setRewardBanner({ code, amount: amt });
    } catch {}
  }, []);

  // Live price-drop alerts for watchlisted templates
  useEffect(() => {
    const off = onDrop((it) => {
      if (!watchRef.current.includes(it.id)) return;
      const now = Date.now();
      if (now - (lastAlertRef.current[it.id] || 0) < 20000) return;
      lastAlertRef.current[it.id] = now;
      const prod = productsRef.current.find((p) => p.id === it.id);
      toast.warning(`Price drop · ${it.discount_pct}% off`, {
        description: `${prod ? prod.title : "A watched template"} just ticked below market — $${it.live_competitor}.`,
        icon: null,
      });
    });
    return off;
  }, [onDrop]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .products({
        software: filters.software,
        tier: filters.tier,
        min_rating: filters.min_rating || undefined,
        sort: filters.sort,
      })
      .then(setProducts)
      .catch((err) => console.error("Failed to load products", err?.message || err))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="relative min-h-screen bg-slate-950">
      <Ticker />
      {refBanner && (
        <div
          data-testid="referral-banner"
          className="flex items-center justify-center gap-3 border-b border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-center text-sm text-emerald-200"
        >
          <Gift className="h-4 w-4 flex-shrink-0 text-emerald-400" />
          <span>
            A friend gave you <span className="font-bold text-emerald-400">{refBanner.discount_pct}% off</span> — applied automatically at checkout.
          </span>
          <button
            data-testid="referral-banner-dismiss"
            onClick={() => setRefBanner(null)}
            aria-label="Dismiss"
            className="ml-1 text-emerald-300/70 transition-colors duration-200 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {rewardBanner && (
        <div
          data-testid="reward-banner"
          className="flex items-center justify-center gap-3 border-b border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-center text-sm text-emerald-200"
        >
          <Wallet className="h-4 w-4 flex-shrink-0 text-emerald-400" />
          <span>
            Your <span className="font-bold text-emerald-400">${rewardBanner.amount.toFixed(2)} referral reward</span> is applied automatically at checkout.
          </span>
          <button
            data-testid="reward-banner-dismiss"
            onClick={() => setRewardBanner(null)}
            aria-label="Dismiss"
            className="ml-1 text-emerald-300/70 transition-colors duration-200 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <Navbar onSearch={() => setShowFilters((v) => !v)} />
      <MoversStrip onSelect={setPreview} />
      <Hero stats={stats} />
      <SpotlightBanner onSelect={setPreview} />
      <MarketBoard onSelect={setPreview} />
      <BundleOffer />

      {/* Marketplace */}
      <section id="marketplace" className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
        <div className="mb-10 flex flex-col justify-between gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-end">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
              Live Catalog
            </span>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tighter text-white sm:text-4xl">
              The Marketplace
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-filter-toggle"
              onClick={() => setShowFilters((v) => !v)}
              className="flex items-center gap-2 border border-white/15 px-3 py-2 text-sm text-slate-300 transition-colors duration-200 hover:border-white/40 lg:hidden"
            >
              <SlidersHorizontal className="h-4 w-4" /> Filters
            </button>
            <div className="relative flex items-center gap-2 border border-white/15 px-3 py-2">
              <ArrowDownWideNarrow className="h-4 w-4 text-slate-400" />
              <select
                data-testid="sort-select"
                value={filters.sort}
                onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
                className="bg-transparent text-sm text-slate-200 outline-none [&>option]:bg-slate-900"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className={`lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`}>
            <div className="lg:sticky lg:top-24">
              <FilterSidebar filters={filters} setFilters={setFilters} />
            </div>
          </div>

          <div className="lg:col-span-9">
            {loading ? (
              <div className="flex items-center justify-center py-32 text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : products.length === 0 ? (
              <div className="border border-white/10 bg-slate-950 py-32 text-center text-slate-400">
                No templates match your filters.
              </div>
            ) : (
              <div
                data-testid="product-grid"
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3"
              >
                {products.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} onPreview={setPreview} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <AutopilotSection samplePosts={samplePosts} />
      <Newsletter />
      <Footer />

      <PreviewModal product={preview} onClose={() => setPreview(null)} />
      <ExitIntentPopup />
    </div>
  );
}
